/*
 * Copyright (c) 2020 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { Injectable } from '@angular/core';
import { HttpService } from '@angular-kit/core';
import { AccountingEntity, AccountingEntityData } from '@teamworks/global';
import { AuthenticationService } from '@angular-kit/authentication';

@Injectable()
export class AccountingService {
  constructor(
    private httpService: HttpService,
    private authenticationService: AuthenticationService
  ) {
  }

  async loadAccountingRecordListByMonth(year: string, month: string): Promise<AccountingEntity[]> {
    const encodedYear = encodeURIComponent(year);
    const encodedMonth = encodeURIComponent(month);
    return this.httpService.get(`api/accounting/accounting-record/${encodedYear}/${encodedMonth}`)
      .then((loadedData: AccountingEntityData[]) => loadedData.map(data => new AccountingEntity(data)));
  }

  async loadAccountingRecord(date: string, index: number): Promise<AccountingEntity> {
    return this.httpService.get(`api/accounting/accounting-record/${date}/${index}`)
      .then((loadedData: AccountingEntityData) => loadedData && new AccountingEntity(loadedData));
  }

  async saveAccountingRecord(data: AccountingEntityData): Promise<AccountingEntity> {
    return this.httpService.put(`api/accounting/accounting-record`, data)
      .then((savedData: AccountingEntityData) => new AccountingEntity(savedData));
  }

  async insertAccountingRecord(data: AccountingEntityData): Promise<AccountingEntity> {
    return this.httpService.post(`api/accounting/accounting-record`, data)
      .then((savedData: AccountingEntityData) => new AccountingEntity(savedData));
  }

  async updateAccountingRecord(date: string, index: number, version: string, data: AccountingEntityData): Promise<AccountingEntity> {
    return this.httpService.put(`api/accounting/accounting-record/${date}/${index}/${encodeURIComponent(version)}`, data)
      .then((savedData: AccountingEntityData) => new AccountingEntity(savedData));
  }

  async deleteAccountingRecord(date: string, index: number): Promise<AccountingEntity> {
    return this.httpService.delete(`api/accounting/accounting-record/${date}/${index}`)
      .then((deletedData: AccountingEntityData) => new AccountingEntity(deletedData));
  }

  async createPdfInvoice(year: string, month: string, id: number, header: string, headerLogo: string, footer: string, fileName: string) {
    const authorization = this.authenticationService.authenticationToken;
    const encodedYear = encodeURIComponent(year);
    const encodedMonth = encodeURIComponent(month);
    const encodedId = encodeURIComponent(id);
    const encodedHeader = header ? encodeURIComponent(header) : '';
    const encodedFooter = footer ? encodeURIComponent(footer) : '';
    const encodedFileName = fileName ? encodeURIComponent(fileName) : '';
    const encodedHeaderLogo = headerLogo ? encodeURIComponent(headerLogo) : '';
    const query = `authorization=${authorization}&header=${encodedHeader}&footer=${encodedFooter}&fileName=${encodedFileName}&headerLogo=${encodedHeaderLogo}`;
    window.open(`api/accounting/pdf/invoice/${encodedYear}/${encodedMonth}/${encodedId}?${query}`);
  }

}
